import { useState } from 'react';
import { ClipboardDocumentIcon, CheckIcon } from '@heroicons/react/24/outline';

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      className="flex items-center gap-1 px-3 py-1 text-sm bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700"
    >
      {copied ? (
        <>
          <CheckIcon className="w-4 h-4 text-green-600 dark:text-green-400" />
          <span className="text-green-600 dark:text-green-400">Copied</span>
        </>
      ) : (
        <>
          <ClipboardDocumentIcon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
          <span className="text-gray-600 dark:text-gray-400">Copy</span>
        </>
      )}
    </button>
  );
}

export function TwitterPreview({ data }) {
  if (!data) return null;

  return (
    <div className="space-y-4">
      {data.single_tweet && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Single Tweet</h3>
            <CopyButton text={data.single_tweet} />
          </div>
          <div className="bg-white dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-800">
            <p className="text-sm dark:text-gray-100">{data.single_tweet}</p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">{data.single_tweet.length}/280</p>
          </div>
        </div>
      )}

      {data.thread?.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Thread ({data.thread.length} tweets)</h3>
            <CopyButton text={data.thread.join('\n\n')} />
          </div>
          <div className="space-y-2">
            {data.thread.map((tweet, idx) => (
              <div key={idx} className="bg-white dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-800">
                <p className="text-sm dark:text-gray-100">{tweet}</p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{tweet.length}/280</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function YouTubePreview({ data }) {
  if (!data) return null;

  const fullScript = [
    data.hook && `[HOOK]\n${data.hook}`,
    data.introduction && `[INTRODUCTION]\n${data.introduction}`,
    ...(data.talking_points || []).map(
      (tp, i) => `[POINT ${i + 1}: ${tp.point}]\n${tp.details}`
    ),
    data.conclusion && `[CONCLUSION]\n${data.conclusion}`,
    data.cta && `[CALL TO ACTION]\n${data.cta}`,
  ].filter(Boolean).join('\n\n');

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">YouTube Script</h3>
        <CopyButton text={fullScript} />
      </div>

      {data.hook && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-xl p-4 border border-yellow-200 dark:border-yellow-800">
          <h4 className="text-xs font-medium text-yellow-700 dark:text-yellow-400 mb-1">Hook</h4>
          <p className="text-sm dark:text-gray-100">{data.hook}</p>
        </div>
      )}

      {data.introduction && (
        <div className="bg-white dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-800">
          <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Introduction</h4>
          <p className="text-sm dark:text-gray-100">{data.introduction}</p>
        </div>
      )}

      {data.talking_points?.map((tp, idx) => (
        <div key={idx} className="bg-white dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-800">
          <h4 className="text-xs font-medium text-indigo-600 dark:text-indigo-400 mb-1">Point {idx + 1}: {tp.point}</h4>
          <p className="text-sm dark:text-gray-100">{tp.details}</p>
        </div>
      ))}

      {data.conclusion && (
        <div className="bg-white dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-800">
          <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Conclusion</h4>
          <p className="text-sm dark:text-gray-100">{data.conclusion}</p>
        </div>
      )}

      {data.cta && (
        <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4 border border-green-200 dark:border-green-800">
          <h4 className="text-xs font-medium text-green-700 dark:text-green-400 mb-1">Call to Action</h4>
          <p className="text-sm dark:text-gray-100">{data.cta}</p>
        </div>
      )}
    </div>
  );
}
